"use client";

import { useId } from "react";
import type { ProductPrimarySelection } from "@/data/product-categories";
import { getSubcategories, PRODUCT_PRIMARIES } from "@/data/product-categories";
import { FUNNEL_TEXT_MAX_WORDS, truncateToMaxWords } from "@/lib/word-limit";

type Variant = "light" | "dark";

const selectLight =
  "min-h-12 w-full rounded-xl border border-zinc-200 bg-white px-3 py-3 text-base font-medium text-zinc-900 shadow-sm outline-none ring-violet-400 focus:ring-2 sm:py-2.5 sm:text-sm";

const selectDark =
  "min-h-12 w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-3 text-base font-medium text-white outline-none ring-fuchsia-500/50 focus:ring-2 sm:py-2.5 sm:text-sm";

const labelLight = "block text-sm font-semibold text-zinc-800";
const labelDark = "block text-sm font-semibold text-zinc-200";

const areaLight =
  "min-h-24 w-full rounded-xl border border-zinc-200 bg-white px-3 py-3 text-base text-zinc-900 shadow-sm outline-none ring-violet-400 focus:ring-2 sm:text-sm";

const areaDark =
  "min-h-24 w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-3 text-base text-white outline-none ring-fuchsia-500/50 focus:ring-2 sm:text-sm";

type Props = {
  variant: Variant;
  primary: ProductPrimarySelection;
  sub: string;
  notes: string;
  onPrimary: (p: ProductPrimarySelection) => void;
  onSub: (s: string) => void;
  onNotes: (s: string) => void;
  primaryQuestion?: string;
};

export function ProductCategoryFields({
  variant,
  primary,
  sub,
  notes,
  onPrimary,
  onSub,
  onNotes,
  primaryQuestion = "What are you trying to sell?",
}: Props) {
  const uid = useId();

  const primarySelectId = `${uid}-product-primary`;
  const subSelectId = `${uid}-product-sub-${primary || "unset"}`;

  const subOptions = primary && primary !== "other" ? getSubcategories(primary) : [];

  const sel = variant === "light" ? selectLight : selectDark;
  const lab = variant === "light" ? labelLight : labelDark;
  const area = variant === "light" ? areaLight : areaDark;
  const hint = variant === "light" ? "text-zinc-500" : "text-zinc-500";

  const handlePrimaryChange = (nextPrimary: ProductPrimarySelection) => {
    onPrimary(nextPrimary);

    if (!nextPrimary) {
      onSub("");
      onNotes("");
      return;
    }

    if (nextPrimary === "other") {
      onSub("");
      return;
    }

    /** Notes are only for “Other”; clear when user picks a normal category. */
    onNotes("");
    onSub(getSubcategories(nextPrimary)[0]?.id ?? "");
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-2">
        <label htmlFor={primarySelectId} className={lab}>
          {primaryQuestion}
        </label>

        <select
          id={primarySelectId}
          name="product-primary"
          autoComplete="off"
          value={primary}
          onChange={(e) => handlePrimaryChange(e.target.value as ProductPrimarySelection)}
          className={`${sel} touch-manipulation`}
        >
          <option value="">Select Category</option>
          {PRODUCT_PRIMARIES.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>

        <p className={`text-xs ${hint}`}>Pick a lane first — then narrow it down.</p>
      </div>

      {primary && primary !== "other" && subOptions.length > 0 ? (
        <div className="space-y-2">
          <label htmlFor={subSelectId} className={lab}>
            Get specific
          </label>

          <select
            id={subSelectId}
            name="product-sub"
            autoComplete="off"
            value={sub}
            onChange={(e) => onSub(e.target.value)}
            className={`${sel} touch-manipulation`}
          >
            <option value="">Select Subcategory</option>
            {subOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {primary === "other" ? (
        <div className="space-y-2">
          <label htmlFor="product-other-desc" className={lab}>
            Describe it in your own words
          </label>
          <textarea
            id="product-other-desc"
            value={notes}
            onChange={(e) => onNotes(truncateToMaxWords(e.target.value, FUNNEL_TEXT_MAX_WORDS))}
            rows={3}
            placeholder="Whatever it is, type it plain."
            className={area}
          />
        </div>
      ) : null}
    </div>
  );
}
